import { accessNested, asList, parseDate } from 'utils/utils';
import preprocessScreenings from 'utils/preprocessScreenings';
import Theater from 'models/Theater';
import Exhibitor from 'models/Exhibitor';
import Screening from 'models/Screening';
import Format from 'models/Format';
import Movie from 'models/Movie';

export default function processScreeningsApi({ data = [], included = [] }, clientGeo, appData, activePage, theaterMasters, firstBatch) {
	if (typeof preprocessScreenings === 'function') {
		const ret = preprocessScreenings(data, included, appData);
		if (ret && ret.data) data = ret.data;
		if (ret && ret.included) included = ret.included;
	}

	activePage = activePage || accessNested(appData, 'pages.showtimes') || {};

	// const formats = included.filter(({ type }) => (type === 'formats')).map((f) => (new Format(f.id, f.attributes)));
	const formatNames = accessNested(activePage, 'data.formatNames', {});
	const formatsLookup = included.filter(({ type }) => type === 'formats').reduce((prev, f) => {
		// if (f.attributes.name === 'standard') return prev;
		const name = f.attributes.name;
		const newName = formatNames[name] || formatNames[name.toLowerCase().replace(/\s/g, '')];
		if (newName) {
			f.attributes.name = newName;
		}
		prev[f.id] = new Format(f.id, f.attributes);
		return prev;
	}, {});
	// let movies = included.filter(({ type }) => (type === 'movies')).map((m) => (new Movie(m.id, m.attributes, formats.find((f) => (f.id === m.attributes.format_id)))));
	const movieNames = accessNested(activePage, 'data.movieNames', {});
	const moviesLookup = included.filter(({ type }) => type === 'movies').reduce((prev, m) => {
		const format = formatsLookup[m.attributes.format_id];
		const newName = movieNames[m.id] || movieNames[m.attributes.provider_id + ':' + m.attributes.provider_movie_id];
		if (newName) {
			m.attributes.title = newName;
		}
		prev[m.id] = new Movie(m.id, m.attributes, format);
		return prev;
	}, {});

	const theaterMasterDocs = included.filter(({ type }) => (type === 'theater_masters')).reduce((prev, t) => {
		prev[t.id] = t.attributes.doc;
		return prev;
	}, {});

	const movies = Object.values(moviesLookup);

	const atomIds = accessNested(activePage, 'data.atomIds', {});
	const www2dMovie = movies.find(m => {
		const isWebedia = m.provider_id === 'wwm' || m.provider_id === 'webus';
		return isWebedia && m.format && m.format.name.toLowerCase() === '2d';
		// ^ Sometimes, Webedia IDs will vary based on the format
		// Atom however should be matched with 2d movie IDs only
	});
	const www2dMovieId = www2dMovie && (www2dMovie.provider_movie_id.split('|')[0]);
	const mainAtom = atomIds.main || www2dMovieId || undefined;
	movies.forEach((m) => {
		if (m.atom) return;
		let atomId = mainAtom;
		const mid = m.provider_id + ':' + m.provider_movie_id;
		if (atomIds[mid]) {
			atomId = atomIds[mid];
		}
		m.atom = atomId;
	});

	const screeningDates = accessNested(activePage, 'data.screeningDates', {});
	const dFrom = parseDate(screeningDates.from);
	const dTo = parseDate(screeningDates.to);
	// TODO: ability to put a list of dates instead of from / to
	const checkScreening = screening => {
		// if (!screening.movie) return false;
		if (screening.isPast) return false;
		if (dFrom && screening.stDate && screening.stDate.date < dFrom) return false;
		if (dTo && screening.stDate && screening.stDate.date > dTo) return false;
		return true;
	};
	const theaterScreenings = data.reduce((all, s) => {
		let movie = moviesLookup[s.attributes.movie_id];
		if (!movie) return all;
		const screening = new Screening(s.id, s.attributes, movie, appData);
		let existing = all[screening.theater_id] || [];
		let add = checkScreening(screening);
		// Do not add dateless screenings if they are not the only screening
		if (!screening.date && existing.length && existing.some(s => s.movie === screening.movie)) {
			add = false;
		}
		// Only allow 1 timeless screening per date
		if (!screening.time && existing.some(s => s.date === screening.date && s.movie === screening.movie)) {
			add = false;
		}
		if (add) {
			// Remove any dateless screening
			if (screening.date && existing.length === 1 && !existing[0].date) {
				existing.shift();
			}
			// Remove potential timeless screening for the current date
			if (screening.time) {
				// Is there a way to optimize that ?
				let noTimeIndex = existing.findIndex(s => !s.time && s.date === screening.date);
				if (noTimeIndex !== -1) {
					existing.splice(noTimeIndex, 1);
				}
			}
			// Add the screening
			if (!all[screening.theater_id]) {
				all[screening.theater_id] = [];
			}
			all[screening.theater_id].push(screening);
		}
		return all;
	}, {});

	const exhibitorsLookup = included.filter(({ type }) => type === 'exhibitors').reduce((prev, e) => {
		prev[e.id] = new Exhibitor(e.id, e.attributes);
		return prev;
	}, {});

	const ignore = accessNested(activePage, 'data.ignoreProviderRegions', {});
	let pinned = asList(accessNested(activePage, 'data.pinnedTheaters'));
	let rawTheaters = included.filter(({ type }) => type === 'theaters');
	const theaters = rawTheaters.map(t => {
		let provider = t.attributes.provider_id;
		if (ignore[provider] && ignore[provider][t.attributes.country]) {
			return;
		}
		return new Theater(
			t.id,
			t.attributes,
			{
				pinned: pinned.indexOf(t.id) !== -1,
				screenings: theaterScreenings[t.id] || [],
				exhibitor: exhibitorsLookup[t.attributes.exhibitor_id],
				theaterMasterDoc: t.attributes.theater_master_id && theaterMasterDocs[t.attributes.theater_master_id],
				clientGeo,
				appData,
				theaterMasters,
				firstBatch,
				activePage
			}
		);
	}).filter(t => t && t.enabled);

	const getUniques = (arr, accessor) => arr.reduce((f, v) => (v = accessor ? accessor(v) : v, f.indexOf(v) === -1 && f.push(v), f), []);
	const finalScreenings = [].concat.apply([], theaters.map(t => t.screenings));
	const finalMovies = getUniques(finalScreenings, s => s.movie).filter(e => e);
	const finalFormats = getUniques(finalMovies, m => m.format);

	return {
		formats:    finalFormats,
		movies:     finalMovies,
		screenings: finalScreenings,
		exhibitors: Object.values(exhibitorsLookup),
		theaters:   theaters
	};
}
